<template>
  <div class="mt-1">
    <div
        class="position-relative border border-2 border-primary rounded-2 overflow-hidden"
        style="height: 48px;"
        ref="sliderContainer"
        @touchstart="startSlide"
        @touchmove="moveSlide"
        @touchend="endSlide"
        @mousedown="startSlide"
        @mousemove="moveSlide"
        @mouseup="endSlide"
        @mouseleave="endSlide"
    >
      <!-- Light grey background -->
      <div class="position-absolute top-0 bottom-0 end-0 start-0 bg-body-secondary"></div>

      <!-- Green progress background -->
      <div
          class="position-absolute top-0 bottom-0 start-0 bg-success"
          :style="{ width: `${(sliderPosition / (containerWidth - buttonWidth)) * 100}%` }"
      ></div>

      <!-- Centered text -->
      <div class="position-absolute top-0 bottom-0 start-0 end-0 d-flex flex-column align-items-center justify-content-center">
        <span class="user-select-none">{{ title }}</span>
        <!-- Optional status line (e.g. the transfer's current status); never blocks the slide -->
        <span v-if="$slots.status" class="user-select-none lh-1" data-test="slide-status"><slot name="status"/></span>
      </div>

      <!-- Sliding button -->
      <div
          ref="slider"
          class="position-absolute top-0 bottom-0 start-0 d-flex align-items-center justify-content-center bg-primary border-end border-start border-2 shadow border-primary"
          :class="{ 'transition-transform': !isDragging }"
          :style="{
          width: '48px',
          transform: `translateX(${sliderPosition}px)`,
        }"
      >
        <IconChevronsRight
            class="text-white"
            :size="24"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { IconChevronsRight } from '@tabler/icons-vue'

// Props
const props = defineProps({
  title: {
    type: String,
    default: 'Slide to confirm'
  }
})

// Emits
const emit = defineEmits(['complete'])

// Refs
const sliderContainer = ref(null)
const slider = ref(null)
const buttonWidth = ref(48)
const isDragging = ref(false)
const startX = ref(0)
const sliderPosition = ref(0)
const containerWidth = ref(0)
const hasEmittedComplete = ref(false)

// Initialize dimensions
onMounted(() => {
  containerWidth.value = sliderContainer.value.offsetWidth
})

const startSlide = (event) => {
  if (hasEmittedComplete.value) return
  isDragging.value = true
  startX.value = event.type === 'mousedown' ? event.clientX : event.touches[0].clientX
}

const moveSlide = (event) => {
  if (!isDragging.value || hasEmittedComplete.value) return

  const currentX = event.type === 'mousemove' ? event.clientX : event.touches[0].clientX
  const diff = currentX - startX.value
  const maxPosition = containerWidth.value - buttonWidth.value

  sliderPosition.value = Math.max(0, Math.min(diff, maxPosition))

  // Only complete when fully at the end (with a small 2px margin of error)
  if (sliderPosition.value >= maxPosition - 2 && !hasEmittedComplete.value) {
    hasEmittedComplete.value = true
    emit('complete')
  }
}

const endSlide = () => {
  if (!isDragging.value || hasEmittedComplete.value) return
  isDragging.value = false
  if (!hasEmittedComplete.value) {
    sliderPosition.value = 0
  }
}
</script>

<style scoped>
.transition-transform {
  transition: transform 0.2s ease-out;
}
</style>